import { useEffect, useState } from "react";
import FormModal from "@/components/FormModal/FormModal";
import { Select } from "@/components/FormControl/Select/Select";
import CheckIcon from "@/components/SVGComponents/CheckIcon";
import { getDisplayName } from "@/lib/user";
import toast from "@/lib/toast";
import userService, { USER_CATEGORY_OPTIONS } from "@/services/api/user";
import type { User, UserCategory } from "@/services/api/user";
import styles from "./UnblockMemberModal.module.scss";

type CategoryOption = { label: string; value: UserCategory };

// Same two options and default as ApplicationReview's approve flow.
const DEFAULT_CATEGORY: CategoryOption = USER_CATEGORY_OPTIONS[0] ?? { label: "Chartered", value: "CHARTERED" };

export interface UnblockMemberModalProps {
  // The member being unblocked, or `null` when the modal is closed.
  user: User | null;
  onClose: () => void;
  onUnblocked?: () => void;
}

const UnblockMemberModal = ({ user, onClose, onUnblocked }: UnblockMemberModalProps) => {
  const [category, setCategory] = useState<CategoryOption>(DEFAULT_CATEGORY);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Preselect the member's current category when it's a known value,
  // otherwise fall back to the default — reset each time the modal opens
  // for a new member.
  useEffect(() => {
    if (!user) return;
    const existing = USER_CATEGORY_OPTIONS.find((option) => option.value === user.userCategory);
    setCategory(existing ?? DEFAULT_CATEGORY);
  }, [user]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!user) return;
    const name = getDisplayName(user);
    setIsSubmitting(true);
    try {
      await userService.unblockUser(user.id, category.value);
      toast.success(`${name} has been unblocked.`);
      onUnblocked?.();
      onClose();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormModal
      isOpen={user !== null}
      onClose={onClose}
      title="Unblock Member"
      subtitle={
        user ? `Restore ${getDisplayName(user)}'s access and set the room they'll rejoin as.` : ""
      }
      onSubmit={handleSubmit}
      submitLabel="Unblock"
      submitIcon={<CheckIcon width={16} height={16} />}
      submitDisabled={isSubmitting}
      className={styles.unblockModal}
    >
      <Select
        name="userCategory"
        label="Category"
        labelVariant="caption"
        options={USER_CATEGORY_OPTIONS}
        value={category}
        isSearchable={false}
        onChange={(option) => {
          if (option && !Array.isArray(option)) {
            setCategory(option as CategoryOption);
          }
        }}
        isDisabled={isSubmitting}
      />
    </FormModal>
  );
};

export default UnblockMemberModal;
